import { inject, onUnmounted, provide, reactive, type InjectionKey } from "vue"

export type FilterRegistration = {
    isActive: () => boolean
    reset: () => void
}

export type FilterRegistry = {
    register: (id: string, filter: FilterRegistration) => void
    unregister: (id: string) => void
    hasActiveFilters: () => boolean
    resetAll: () => void
}

const filterRegistryKey = Symbol("FilterRegistry") as InjectionKey<FilterRegistry>

/**
 * Creates a filter registry that child components can register with.
 * Call this in the parent component that contains the filters.
 */
export function createFilterRegistry(): FilterRegistry {
    const filters = reactive(new Map<string, FilterRegistration>())

    const registry: FilterRegistry = {
        register(id: string, filter: FilterRegistration) {
            filters.set(id, filter)
        },
        unregister(id: string) {
            filters.delete(id)
        },
        hasActiveFilters() {
            for (const filter of filters.values()) {
                if (filter.isActive()) {
                    return true
                }
            }
            return false
        },
        resetAll() {
            for (const filter of filters.values()) {
                filter.reset()
            }
        },
    }

    provide(filterRegistryKey, registry)

    return registry
}

/**
 * Registers a filter with the parent's filter registry.
 * Call this in filter components.
 * If no registry is provided by a parent, the filter will work standalone.
 */
export function useFilterRegistration(id: string, filter: FilterRegistration) {
    const registry = inject<FilterRegistry | null>(filterRegistryKey, null)

    if (registry) {
        registry.register(id, filter)

        onUnmounted(() => {
            registry.unregister(id)
        })
    }
}
